import React, { useState, useContext, useRef } from 'react';

import PropTypes from 'prop-types';
import { Segment, Icon, Popup, Modal, Button } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';

import CloseableModal from 'components/common/CloseableModal';
import ConfirmModal from 'components/common/ConfirmModal';
import { PopupUtils } from 'components/modules/foundation/services/popup';

import { ACTION_NAMES, DELETE_CONFIRM_BY_ACTION } from './CustomPageElement.constants';
import {
  getElementDisplayOptions,
  canAddMoreColumns,
  getColumnMaxWidth,
  cancelGridDelete,
  confirmGridDelete,
} from './CustomPageElement.utils';
import { ContentNoticeLabel } from './components';
import API from '../../../../../libs/api';
import ErrorHandler, { getErrorMessage } from '../../../../../libs/errors';
import { AUTO_ADJUSTED_COLUMN_SIZE_MODE } from '../../constants/types';
import { CustomPagesContext } from '../../contexts/CustomPages.context';
import {
  getComponentConfigurationIdsFromColumn,
  getComponentConfigurationIdsFromRow,
} from '../../services/page-state';
import { ColumnSegmentPropType, ColumnPropType } from '../../types/column.types';
import { RowPropType } from '../../types/row.types';
import EditGridElementModal from '../EditGridElementModal';
import EditPageElementModal from '../EditPageElementModal';
import EditPageElementOptions from '../EditPageElementOptions';

import './CustomPageElement.scss';

const propTypes = {
  segment: ColumnSegmentPropType.isRequired,
  row: RowPropType.isRequired,
  column: ColumnPropType.isRequired,
  height: PropTypes.string.isRequired,
};

const defaultProps = {};

export default function CustomPageElement({ segment, row, column, height }) {
  const triggerRef = useRef(null);

  const { elements } = segment;
  const {
    getCurrentNode,
    setNode,
    updateBlock,
    removeBlock,
    addBlockAbove,
    addBlockBelow,
    activePage,
    updateColumn,
    updateRow,
    addColumnToLeft,
    addColumnToRight,
    removeColumn,
    addRowAbove,
    addRowBelow,
    savePageNode,
    removeNode,
    isValidElement,
  } = useContext(CustomPagesContext);
  const { page } = activePage;
  const { columns, columnSizingMode } = row;

  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const [showGridColumnModal, setShowGridColumnModal] = useState(false);
  const [showGridRowModal, setShowGridRowModal] = useState(false);
  const [showElementContentModal, setShowElementContentModal] = useState(false);
  const [loadingActionName, setLoadingActionName] = useState('');
  const [actionError, setActionError] = useState('');
  const [pendingDeleteAction, setPendingDeleteAction] = useState('');

  function openOptionsMenu() {
    setIsOptionsMenuOpen(true);
  }

  function closeOptionsMenu() {
    if (loadingActionName) return;

    setIsOptionsMenuOpen(false);
  }

  function toggleGridColumnModal() {
    setShowGridColumnModal((prevShowGridColumnModal) => !prevShowGridColumnModal);
  }

  function toggleGridRowModal() {
    setShowGridRowModal((prevShowGridRowModal) => !prevShowGridRowModal);
  }

  function toggleElementContentModal() {
    setShowElementContentModal((prevShowElementContentModal) => !prevShowElementContentModal);
  }

  async function performNodeOperation({
    updateFunc,
    updateFuncArgs = [],
    actionName = '',
    onPostSave = () => null,
    closeOptionsModal = false,
  }) {
    if (actionName) {
      setLoadingActionName(actionName);
    }

    const prevNode = getCurrentNode();

    try {
      const updatedNode = updateFunc(...updateFuncArgs);
      await savePageNode(updatedNode);
    } catch (e) {
      ErrorHandler.capture(e);
      setActionError(getErrorMessage(e));
      setNode(prevNode);
    }

    if (actionName) {
      setLoadingActionName('');
    }

    await onPostSave();
    if (closeOptionsModal) {
      setIsOptionsMenuOpen(false);
    }
  }

  async function onElementContentSave(nextElement) {
    const { id: elementId } = elements[0];
    const { type } = nextElement;

    await performNodeOperation({
      updateFunc: updateBlock,
      updateFuncArgs: [elementId, type, nextElement],
      onPostSave: () => setShowElementContentModal(false),
    });
  }

  async function onElementContentDelete() {
    const { id } = elements[0];

    await performNodeOperation({
      updateFunc: removeBlock,
      updateFuncArgs: [id],
      onPostSave: () => setShowElementContentModal(false),
    });
  }

  async function onElementColumnSave(nextColumn) {
    const { id } = nextColumn;

    await performNodeOperation({
      updateFunc: updateColumn,
      updateFuncArgs: [id, nextColumn],
      onPostSave: () => setShowGridColumnModal(false),
    });
  }

  async function onElementRowSave(nextRow) {
    const { id } = nextRow;

    await performNodeOperation({
      updateFunc: updateRow,
      updateFuncArgs: [id, nextRow],
      onPostSave: () => setShowGridRowModal(false),
    });
  }

  async function onAddColumnLeft() {
    const { id } = column;

    await performNodeOperation({
      updateFunc: addColumnToLeft,
      updateFuncArgs: [id],
      actionName: ACTION_NAMES.onAddColumnLeft,
      closeOptionsModal: true,
    });
  }

  async function onAddColumnRight() {
    const { id } = column;

    await performNodeOperation({
      updateFunc: addColumnToRight,
      updateFuncArgs: [id],
      actionName: ACTION_NAMES.onAddColumnRight,
      closeOptionsModal: true,
    });
  }

  async function onDeleteColumn() {
    const { id } = column;

    const deleteColumnContent = async () => {
      const ids = getComponentConfigurationIdsFromColumn(column);

      await Promise.all(ids.map((currentId) => API.removeCustomPageContent(currentId)));
    };

    await performNodeOperation({
      updateFunc: removeColumn,
      updateFuncArgs: [id],
      actionName: ACTION_NAMES.onDeleteColumn,
      onPostSave: deleteColumnContent,
      closeOptionsModal: true,
    });
  }

  async function onAddRowAbove() {
    const { id } = row;

    await performNodeOperation({
      updateFunc: addRowAbove,
      updateFuncArgs: [id],
      actionName: ACTION_NAMES.onAddRowAbove,
      closeOptionsModal: true,
    });
  }

  async function onAddRowBelow() {
    const { id } = row;

    await performNodeOperation({
      updateFunc: addRowBelow,
      updateFuncArgs: [id],
      actionName: ACTION_NAMES.onAddRowBelow,
      closeOptionsModal: true,
    });
  }

  async function onDeleteRow() {
    const { id } = row;

    const deleteRowContent = async () => {
      const ids = getComponentConfigurationIdsFromRow(row);
      await Promise.all(ids.map((currentId) => API.removeCustomPageContent(currentId)));
    };

    await performNodeOperation({
      updateFunc: removeNode,
      updateFuncArgs: [id],
      actionName: ACTION_NAMES.onDeleteRow,
      onPostSave: deleteRowContent,
      closeOptionsModal: true,
    });
  }

  const pendingDeleteConfirm = DELETE_CONFIRM_BY_ACTION[pendingDeleteAction];
  const isDeleteConfirmModalOpen = Boolean(pendingDeleteConfirm);
  const isDeleteLoading = Boolean(DELETE_CONFIRM_BY_ACTION[loadingActionName]);

  async function onAddBlockAbove() {
    const { id: elementId } = elements[0];

    await performNodeOperation({
      updateFunc: addBlockAbove,
      updateFuncArgs: [elementId],
      actionName: ACTION_NAMES.onAddBlockAbove,
      closeOptionsModal: true,
    });
  }

  async function onAddBlockBelow() {
    const { id: elementId } = elements[0];

    await performNodeOperation({
      updateFunc: addBlockBelow,
      updateFuncArgs: [elementId],
      actionName: ACTION_NAMES.onAddBlockBelow,
      closeOptionsModal: true,
    });
  }

  function isValidSegment() {
    if (_isEmpty(elements)) return false;

    const element = elements[0];

    return isValidElement(element);
  }

  const maxColumnWidth = getColumnMaxWidth(row, column);
  const isAutoAdjustMode = columnSizingMode === AUTO_ADJUSTED_COLUMN_SIZE_MODE;

  return (
    <div className="custom-page-element" style={{ height }}>
      <Segment onMouseLeave={closeOptionsMenu}>
        {!isValidSegment() && <ContentNoticeLabel />}
        {getElementDisplayOptions(segment).map(({ id, icon, label }) => (
          <Popup
            key={id}
            className="element-options-popup"
            trigger={
              <div
                key={id}
                onMouseEnter={openOptionsMenu}
                className="element-display"
                ref={triggerRef}
              >
                <Icon name={icon} key={icon} size="huge" />
                <div>{label}</div>
              </div>
            }
            open={isOptionsMenuOpen}
            position={PopupUtils.getSafePosition(triggerRef, {
              position: 'right center',
              rightPosition: 'left center',
              referenceWindow: 'right center',
            })}
            basic
          >
            <EditPageElementOptions
              onEditElementContent={toggleElementContentModal}
              onEditElementColumn={toggleGridColumnModal}
              onEditElementRow={toggleGridRowModal}
              onSplitColumn={{
                onAddColumnLeft,
                onAddColumnRight,
                onAddRowAbove,
                onAddRowBelow,
                onAddBlockAbove,
                onAddBlockBelow,
              }}
              onDelete={{
                onDeleteColumn: () => setPendingDeleteAction(ACTION_NAMES.onDeleteColumn),
                onDeleteRow: () => setPendingDeleteAction(ACTION_NAMES.onDeleteRow),
              }}
              disabled={{
                onAddColumnLeft: !canAddMoreColumns(columns),
                onAddColumnRight: !canAddMoreColumns(columns),
                onAddRowAbove: false,
                onAddRowBelow: false,
                onDeleteColumn: false,
                onDeleteRow: false,
                onAddBlockAbove: false,
                onAddBlockBelow: false,
              }}
              loading={{
                onAddColumnLeft: loadingActionName === ACTION_NAMES.onAddColumnLeft,
                onAddColumnRight: loadingActionName === ACTION_NAMES.onAddColumnRight,
                onAddRowAbove: loadingActionName === ACTION_NAMES.onAddRowAbove,
                onAddRowBelow: loadingActionName === ACTION_NAMES.onAddRowBelow,
                onDeleteColumn: loadingActionName === ACTION_NAMES.onDeleteColumn,
                onDeleteRow: loadingActionName === ACTION_NAMES.onDeleteRow,
                onAddBlockAbove: loadingActionName === ACTION_NAMES.onAddBlockAbove,
                onAddBlockBelow: loadingActionName === ACTION_NAMES.onAddBlockBelow,
              }}
            />
          </Popup>
        ))}
      </Segment>
      {showElementContentModal && (
        <EditPageElementModal
          open
          element={elements[0]}
          page={page}
          onCancel={toggleElementContentModal}
          onDelete={onElementContentDelete}
          onSave={onElementContentSave}
        />
      )}
      {showGridColumnModal && (
        <EditGridElementModal
          open
          element={column}
          onCancel={toggleGridColumnModal}
          onSave={onElementColumnSave}
          editWidthSettings={{ disabled: isAutoAdjustMode, maxColumnWidth }}
        />
      )}
      {showGridRowModal && (
        <EditGridElementModal
          open
          element={row}
          onCancel={toggleGridRowModal}
          onSave={onElementRowSave}
        />
      )}
      {actionError && (
        <CloseableModal
          className="custom-page-element-error-modal"
          open
          header="Error Updating Custom Page"
          onClose={() => setActionError('')}
          dimmer="inverted"
          size="mini"
        >
          <Modal.Content>{actionError}</Modal.Content>
        </CloseableModal>
      )}
      <ConfirmModal
        open={isDeleteConfirmModalOpen}
        onClose={() => cancelGridDelete(isDeleteLoading, setPendingDeleteAction)}
        header={pendingDeleteConfirm?.header}
        content={pendingDeleteConfirm?.content}
        onCancel={() => cancelGridDelete(isDeleteLoading, setPendingDeleteAction)}
        onConfirm={() =>
          confirmGridDelete(
            pendingDeleteAction,
            {
              [ACTION_NAMES.onDeleteColumn]: onDeleteColumn,
              [ACTION_NAMES.onDeleteRow]: onDeleteRow,
            },
            setPendingDeleteAction,
          )
        }
        className="delete-page-grid-modal"
        confirmButton={
          <Button
            className="delete-red"
            content="YES"
            disabled={isDeleteLoading}
            loading={isDeleteLoading}
          />
        }
        cancelButton={
          <Button className="action-button-gray" content="CANCEL" disabled={isDeleteLoading} />
        }
      />
    </div>
  );
}

CustomPageElement.propTypes = propTypes;
CustomPageElement.defaultProps = defaultProps;
